/**
 * schoolYear — canonical нормализатор за учебна година.
 *
 * Background: учебната година се съхранява в НЕсъгласувани формати из системата
 * — classes ползват slash ("2025/2026"), children/някои hooks ползват dash
 * ("2025-2026"). Това чупи всяка проверка с точно сравнение на низа:
 * uniqueness guard в createClass и conflict detection в copyClassesToNewYear
 * не разпознават "3А"+"2025/2026" като дубликат на "3А"+"2025-2026" → влизат
 * дублирани класове. Същият клас bug като dayOfWeek (кирилица vs английски).
 *
 * Canonical формат: DASH "2025-2026" — съответства на системната convention
 * (timestampHelper.normalizeSchoolYear + isValidSchoolYear в settings.ts, което
 * валидира YYYY-YYYY и отхвърля slash). Този helper е single source of truth за
 * shared (frontend+backend) — ползвай го, преди да сравняваш/записваш година.
 */

/**
 * Нормализира учебна година към canonical dash формат ("2025-2026").
 * Приема slash/dash/whitespace вариации. Празен вход → "".
 */
export function normalizeSchoolYear(y: string | undefined | null): string {
  return String(y ?? '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/\//g, '-');
}

/**
 * Една и съща учебна година ли са двете стойности.
 *
 * 🔴 ЕДИН нормализатор за двата екрана. Измерено: `normalizeSchoolYear` тук прави `trim`,
 * маха ВСИЧКИ интервали и заменя ВСИЧКИ наклонени черти, докато
 * `functions/src/utils/timestampHelper.ts` заменя САМО първата и не тримва. Докато R11
 * филтрираше категориите с различни функции на двата пътя, стойност като „ 2026 / 2027 "
 * се приемаше на единия екран и отпадаше на другия — тоест същият слот получаваше брой
 * занятия там, където го няма тук.
 *
 * Празна стойност НЕ съвпада с нищо: липсваща година не е „всяка година".
 */
export function isSameSchoolYear(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizeSchoolYear(a);
  return left !== '' && left === normalizeSchoolYear(b);
}

/**
 * Cyrillic→Latin confusables — визуално еднакви глифи с различни кодови точки.
 * Класовите имена смесват двете азбуки ("3А" с кирилско А vs "3A" с латинско A),
 * което прави два визуално еднакви класа да изглеждат различни за exact-match
 * проверките. Fold-ваме към латиница, за да ги третираме като едно.
 */
const CONFUSABLES: Record<string, string> = {
  А: 'A', В: 'B', Е: 'E', К: 'K', М: 'M', Н: 'H',
  О: 'O', Р: 'P', С: 'C', Т: 'T', Х: 'X', У: 'Y',
  а: 'a', е: 'e', о: 'o', р: 'p', с: 'c', у: 'y',
  х: 'x', к: 'k', м: 'm', т: 't',
};

/**
 * Нормализира текст за сравнение: trim + collapse spaces + fold confusables,
 * ПОСЛЕ lowercase. Fold-ваме преди lowercase, защото картата покрива главно
 * UPPERCASE кирилица (секциите са главни: 3А, 3В...) — иначе "3В" (Cyr) и "3B"
 * (Lat) не биха се изравнили.
 */
export function foldConfusables(s: string | undefined | null): string {
  return String(s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .split('')
    .map((ch) => CONFUSABLES[ch] ?? ch)
    .join('')
    .toLowerCase();
}

/**
 * Композитен ключ за откриване на дублирани класове. Хваща дубликати с
 * разминаващ се формат на годината (slash vs dash) И с Cyrillic/Latin
 * confusables в името ("3А" vs "3A"). Single source of truth — ползвай и в
 * guards (createClass/copyClassesToNewYear), и във validateScheduleData.
 */
export function classDedupeKey(name: string | undefined | null, schoolYear: string | undefined | null): string {
  return `${foldConfusables(name)}|${normalizeSchoolYear(schoolYear)}`;
}

/**
 * Двата приети записа на една учебна година — за заявки `where(..., 'in', …)`.
 *
 * Годината се съхранява НЕсъгласувано из базата (slash в categoryDefinitions,
 * dash другаде). Всяко четене, което сравнява точно, трябва да пита и за двата
 * варианта, иначе връща празно за напълно налични данни.
 */
export function schoolYearVariants(year: string | undefined | null): [string, string] {
  const dash = normalizeSchoolYear(year);
  return [dash.replace('-', '/'), dash];
}

/**
 * Канoничната подредба на категориите: `sortOrder`, после име по българска
 * локала. Изнесена, защото я прилагат ДВЕ страни — `listCategories` на сървъра
 * и живият прочит на клиента. Различна подредба на двете места значи, че
 * списъкът се пренарежда под пръстите на потребителя при всяко обновяване.
 */
export function sortCategoryDefs<T extends { sortOrder?: number; name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ao = a.sortOrder ?? 999;
    const bo = b.sortOrder ?? 999;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name, 'bg');
  });
}

/**
 * Копие на категория за ДРУГА учебна година, без полетата, привързани към старата.
 *
 * Днес това е само `startDate` (изключението „начало"): дата от 2026-2027, пренесена в
 * 2027-2028, сочи МИНАЛО — курсът или тръгва година по-рано, или изчислителят не намира
 * нито едно занятие в прозореца и календарът мълчи.
 *
 * 🔴 Функцията съществува, за да има ЕДИН източник: категория стига до нова година по ДВА
 * пътя (`copyCategoriesToYear` и `copyClassesToNewYear`), и двата с `{...cat}` spread, тоест
 * ново поле на категорията се пренася автоматично и по двата, а поправка само на единия
 * оставя другия да носи датата. Ново привързано към годината поле се добавя ТУК.
 */
export function stripYearSpecificCategoryFields<T extends { startDate?: string }>(
  category: T,
): Omit<T, 'startDate'> {
  const { startDate: _boundToOldYear, ...rest } = category;
  return rest;
}
